import { Auth } from '@d-id/client-sdk';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState } from 'react';

import { useFlag } from '@/services/featureFlags';
import { useCreateSession, useGetCampaign, useSendAnalytic } from '../api';
import { CampaignCard, EmbeddedCampaignCard } from './CampaignCard';
import { NotFound } from './NotFound';

const StyledCircularProgress = styled(CircularProgress)`
    margin: auto;
`;

interface Props {
    campaignId: string;
    clientKey: string;
    UID: string;
    embed: boolean;
    inputFields?: Record<string, string>;
}

export const Campaign = ({ campaignId, clientKey, UID, embed, inputFields = {} }: Props) => {
    const [sessionId, setSessionId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const isEmbedEnabled = useFlag('is-embed-campaign-enabled', false);

    const { data: campaign, isError: isCampaignError } = useGetCampaign({
        campaignId,
        clientKey,
        onError: () => setIsLoading(false),
    });
    const { mutateAsync: createSession, isError: isSessionError } = useCreateSession({
        campaignId,
        clientKey,
        onSettled: () => setIsLoading(false),
    });
    const { mutate: sendAnalytic } = useSendAnalytic({ campaignId, clientKey, sessionId });

    useEffect(() => {
        const init = async () => {
            if (!campaign) {
                return;
            }

            const sessionResponse = await createSession({ unique_id: UID, query_params: inputFields });
            setSessionId(sessionResponse);
        };
        init();
    }, [campaignId, clientKey, campaign, createSession, UID, inputFields]);

    if (isLoading) {
        return <StyledCircularProgress size="95px" />;
    }

    if (!campaign || !sessionId || isCampaignError || isSessionError) {
        return <NotFound />;
    }

    const auth: Auth = { clientKey, type: 'key' };

    return isEmbedEnabled && embed ? (
        <EmbeddedCampaignCard campaign={campaign} inputFields={inputFields} sendAnalytic={sendAnalytic} auth={auth} />
    ) : (
        <CampaignCard campaign={campaign} inputFields={inputFields} sendAnalytic={sendAnalytic} auth={auth} />
    );
};
